import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { PlacementErrorType } from '@frontend/sports/types/betslip';
import { Observable, map, share } from 'rxjs';

import { BetslipType } from '../../../../../../core/betslip-type';
import { BetslipPick } from '../../../../../../core/picks/betslip-pick';
import { PickOddsState } from '../../../../../../core/picks/pick-models';
import { flattenGroupPicksIfAny } from '../../../../../../core/utils';
import BetslipPickUtilsService from '../../../../../picks/services/betslip-pick-utils.service';
import { OddsChangedPreCheckError } from '../../../../../validation/errors/pre-check/odds-changed-pre-check-error';
import { ResultError } from '../../../../../validation/errors/result/result-error';
import { getSlipErrorsForType } from '../../../../../validation/services/utils/betslip-errors-state-utils';
import {
    OverallMaxWinPerUserHintType,
    getClosedLegIdsWithinValidGroups,
    getStakeUpdateErrors,
    getSuspendedLegIdsWithinValidGroups,
    isBetPlacementBlockingError,
    isOverallMaxWinPerUserExceededError,
    isPickStatusOddsChanged,
    pickErrorsHasBlockingErrors,
} from '../../../../../validation/services/utils/betslip-errors-utils';
import { IPlaceBetErrorState, PlaceButtonBaseState } from '../../../../place-button.models';
import { IPlaceButtonState, PlaceButtonMode } from '../../../../state';
import { TotalStake, getTotalStakesForCurrentType, isFreeBetStake } from '../../../stake/stake-utils';
import { PlaceButtonActionStateService } from '../../place-button-action-state.service';
import { PlaceButtonBaseStateService } from '../../place-button-base-state.service';
import { IPlaceButtonStateService } from '../interfaces/place-button-interfaces';

@Injectable()
export class BetslipPlaceButtonStateService implements IPlaceButtonStateService {
    state$: Observable<IPlaceButtonState>;

    constructor(
        private betslipPickUtils: BetslipPickUtilsService,
        private actionStateService: PlaceButtonActionStateService,
        baseStateService: PlaceButtonBaseStateService,
        private betslipConfig: BetslipConfig,
    ) {
        this.state$ = baseStateService.baseState$.pipe(
            map((baseState) => this.getMainState(baseState)),
            share(),
        );
    }

    private getMainState({ errorState, externalState }: PlaceButtonBaseState): IPlaceButtonState {
        const selectedPicks = this.betslipPickUtils.getSelectedPicks(errorState.types, errorState.pickState.pickList);
        const lockedPicksCount = selectedPicks.filter((p) => p.currentPrice && p.oddsState === PickOddsState.Locked).length;
        const closedPicksCount = selectedPicks.filter((p) => p.currentPrice && p.oddsState === PickOddsState.Closed).length;
        const closedGroupPicksCount = getClosedLegIdsWithinValidGroups(selectedPicks, errorState.errors.pickErrors[BetslipType.Combo]).length;
        const suspendedGroupPickLegsCount = getSuspendedLegIdsWithinValidGroups(
            selectedPicks,
            errorState.errors.pickErrors[BetslipType.Combo],
        ).length;

        const type = errorState.types.base.currentSelectedType!;

        const pickErrors: ResultError[][] = Object.values(errorState.errors.pickErrors[type]);

        const selectedPickErrors = pickErrors.flatMap((e) => e).filter((e: ResultError) => selectedPicks.some((p) => e.pickId === p.id.toString()));
        const oddsChanged = selectedPickErrors.some(isPickStatusOddsChanged);
        const oddsAccepted = !selectedPickErrors.some((e) => e instanceof OddsChangedPreCheckError);

        const flattenedPicks = flattenGroupPicksIfAny(selectedPicks);
        const flattenedPicksErrors = pickErrors
            .flatMap((e) => e)
            .filter((e: ResultError) => flattenedPicks.some((p) => e.pickId === p.id.toString()));
        const hasComboPreventionError = flattenedPicksErrors.some((e) => e.type === PlacementErrorType.ComboPrevention);

        const hasLivePicks = selectedPicks.some((p) => p.isLive);

        const stakeUpdateErrors = this.betslipConfig.isMinStakeButtonEnabled ? getStakeUpdateErrors(errorState.errors.slipErrors) : [];

        const action = this.actionStateService.getAction({
            isAuthenticated: errorState.placeButton.isAuthenticated,
            mode: errorState.placeButton.mode,
            hasLockedPick: lockedPicksCount + closedPicksCount + closedGroupPicksCount > 0,
            hasLivePicks,
            oddsChanged,
            oddsAccepted,
            hasComboPreventionError,
            betslipErrors: errorState.errors.betslipErrors,
            hasSuspendedPicks: suspendedGroupPickLegsCount > 0,
            stakeUpdateErrors,
        });

        const stakes = getTotalStakesForCurrentType(errorState.types, errorState.rewardTokens);
        const isFreeBet = isFreeBetStake(stakes);

        const isValid = this.checkButtonIsValid(errorState, selectedPicks, hasComboPreventionError, type, stakes);

        const isProcessing = Boolean(errorState.placeButton.mode & PlaceButtonMode.Processing) || externalState.isQuickDepositProcessing;

        return {
            hasLivePicks,
            hasComboPreventionError,
            oddsChanged,
            oddsAccepted,
            changedPicks: {
                unlockedPicks: 0,
                lockedPicks: lockedPicksCount + closedGroupPicksCount,
                closedPicks: closedPicksCount,
                suspendedPicks: suspendedGroupPickLegsCount,
            },
            action,
            isValid,
            isFreeBet,
            isProcessing,
            stakes,
            stakeUpdateErrors,
            isLinear: false,
        };
    }

    private checkButtonIsValid(
        errorsState: IPlaceBetErrorState,
        selectedPicks: BetslipPick[],
        hasComboPreventionError: boolean,
        betslipType: BetslipType,
        stakes: TotalStake[],
    ): boolean {
        const hasOverallMaxWinPerUserExceededErrorWithHint = errorsState.errors.betslipErrors.some((error) =>
            isOverallMaxWinPerUserExceededError(error, OverallMaxWinPerUserHintType.WithHint),
        );

        const allStakesSet = stakes.every((s) => s.stake || s.freeBetAmount);

        const hasError =
            errorsState.errors.betslipErrors.some(isBetPlacementBlockingError) ||
            errorsState.errors.betslipTypeErrors[betslipType].some(isBetPlacementBlockingError) ||
            pickErrorsHasBlockingErrors(errorsState.errors.pickErrors[betslipType]) ||
            getSlipErrorsForType(betslipType, errorsState.errors.slipErrors, errorsState.types, false).some(isBetPlacementBlockingError);

        return (
            allStakesSet &&
            selectedPicks.every((p) => !!p.currentPrice) &&
            (!hasError || hasOverallMaxWinPerUserExceededErrorWithHint) &&
            !hasComboPreventionError &&
            errorsState.placeButton.isValid
        );
    }
}
